import React, { useState, useMemo, useRef, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Dna,
  ArrowUpDown,
  Filter,
  Download,
  Search,
  Network,
  Users,
  GitMerge,
  List,
  Trash2
} from "lucide-react";
import { KinshipRecord } from '@/lib/mock-data';
import { cn } from '@/lib/utils';
import { ComprehensiveFamilyTree, Pass1CombinatorialList } from '@/components/pedigree-chart';
import { useCases, useKinshipRecords, useUpdateProfile, useDeleteCase } from '@/hooks/useKinshipData';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

type SortField = 'relative1' | 'relative2' | 'parentChildLR' | 'fullSiblingLR' | 'extendedFamilyLR';
type SortDirection = 'asc' | 'desc';

interface EditProfileState {
  id: string;
  profileId: string;
  name: string;
  gender: 'M' | 'F' | 'U';
  age?: string;
  caseId: string;
  kit: string;
  ageType: 'dob' | 'age';
  dobDay: string;
  dobMonth: string;
  dobYear: string;
  ageYears: string;
  ageMonths: string;
}

// Calculate decimal age from years and months (e.g., 50 years 5 months = 50.4)
function calculateDecimalAge(years: string, months: string): string {
  const y = parseInt(years) || 0;
  const m = parseInt(months) || 0;
  if (y === 0 && m === 0) return '';
  const decimal = y + (m / 12);
  return decimal.toFixed(1);
}

// Calculate age from date of birth
function calculateAgeFromDOB(day: string, month: string, year: string): { years: number; months: number; decimal: string } | null {
  const y = parseInt(year);
  const m = parseInt(month);
  const d = parseInt(day) || 1;

  if (!y || y < 1900 || y > new Date().getFullYear()) return null;

  const birthDate = new Date(y, (m || 1) - 1, d);
  const today = new Date();

  let years = today.getFullYear() - birthDate.getFullYear();
  let months = today.getMonth() - birthDate.getMonth();

  if (months < 0 || (months === 0 && today.getDate() < birthDate.getDate())) {
    years--;
    months += 12;
  }
  if (today.getDate() < birthDate.getDate()) {
    months--;
    if (months < 0) months += 12;
  }

  const decimal = years + (months / 12);
  return { years, months, decimal: decimal.toFixed(1) };
}

export default function Dashboard({ labId, caseId, caseRunId }: { labId?: string; caseId?: string; caseRunId?: string }) {
  // Fetch all cases
  const { data: cases, isLoading: casesLoading } = useCases();
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(`${labId}_${caseId}_${caseRunId}` || null);

  // Select the first case by default once cases are loaded
  const currentCase = cases?.find(c => c.id === selectedCaseId) || cases?.[0];

  // Fetch kinship records for the current case
  const { data: kinshipData, isLoading: kinshipLoading } = useKinshipRecords(currentCase?.id || '');
  const updateProfile = useUpdateProfile();
  const deleteCase = useDeleteCase();

  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [selectedRecord, setSelectedRecord] = useState<string | null>(null);

  // Edit Profile State
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState<EditProfileState | null>(null);

  // Column resize state
  const [columnWidths, setColumnWidths] = useState<{ [key: string]: number }>({
    relationship: 120,
    relative1: 180,
    relative2: 180,
    commonLoci: 80,
    matchingLoci: 80,
    mac: 60,
    parentChildLR: 110,
    fullSiblingLR: 110,
    extendedLR: 100
  });

  const resizingColumn = useRef<string | null>(null);
  const startX = useRef<number>(0);
  const startWidth = useRef<number>(0);

  const handleMouseDown = useCallback((e: React.MouseEvent, columnKey: string) => {
    e.preventDefault();
    resizingColumn.current = columnKey;
    startX.current = e.clientX;
    startWidth.current = columnWidths[columnKey];

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [columnWidths]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!resizingColumn.current) return;

    const diff = e.clientX - startX.current;
    const newWidth = Math.max(50, startWidth.current + diff);

    setColumnWidths(prev => ({
      ...prev,
      [resizingColumn.current!]: newWidth
    }));
  }, []);

  const handleMouseUp = useCallback(() => {
    resizingColumn.current = null;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  }, [handleMouseMove]);

  // Transform API data to match the expected KinshipRecord format
  const data: KinshipRecord[] = useMemo(() => {
    console.log("Mapping kinship data:", kinshipData);
    if (!kinshipData) return [];
    return kinshipData.map((record: any) => ({
      id: record.id,
      caseId: record.caseId,
      createdDate: new Date(record.createdAt).toLocaleDateString(),
      relationship: record.relationship,
      relative1: {
        id: record.relative1.id,
        name: record.relative1.name,
        gender: record.relative1.gender as 'M' | 'F',
        age: record.relative1.age || 'Unspecified',
        kit: record.relative1.kit,
        profileId: record.relative1.profileId
      },
      relative2: {
        id: record.relative2.id,
        name: record.relative2.name,
        gender: record.relative2.gender as 'M' | 'F',
        age: record.relative2.age || 'Unspecified',
        kit: record.relative2.kit,
        profileId: record.relative2.profileId
      },
      commonLoci: record.commonLoci,
      matchingAlleles: record.matchingAlleles,
      mac: record.mac,
      parentChildLR: record.parentChildLR,
      fullSiblingLR: record.fullSiblingLR,
      extendedFamilyLR: record.extendedFamilyLR
    }));
  }, [kinshipData]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedData = [...data].sort((a, b) => {
    if (!sortField) return 0;

    let valA, valB;

    if (sortField === 'relative1') {
      valA = a.relative1.name;
      valB = b.relative1.name;
    } else if (sortField === 'relative2') {
      valA = a.relative2.name;
      valB = b.relative2.name;
    } else {
      valA = a[sortField];
      valB = b[sortField];
    }

    if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
    if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const formatNumber = (num: number) => {
    if (num === 0) return "0.000";
    if (num >= 1000 || num <= 0.001) return num.toExponential(3);
    return num.toFixed(3);
  };

  const getGenderIcon = (gender: 'M' | 'F') => {
    return (
      <span className={cn(
        "inline-flex items-center justify-center w-5 h-5 border-2 text-xs font-bold shadow-sm",
        gender === 'M' ? "rounded-none bg-blue-50 border-blue-200 text-blue-700" : "rounded-full bg-pink-50 border-pink-200 text-pink-700"
      )}>
        {gender}
      </span>
    );
  };


  // Show loading state
  if (casesLoading || kinshipLoading) {
    return (
      <div className="min-h-screen bg-background p-6 flex items-center justify-center">
        <div className="text-center space-y-4">
          <Dna className="w-12 h-12 text-primary animate-pulse mx-auto" />
          <p className="text-muted-foreground">Loading kinship data...</p>
        </div>
      </div>
    );
  }

  // Show error state if no case found
  if (!currentCase) {
    return (
      <div className="min-h-screen bg-background p-6 flex items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-muted-foreground">No case data found. Please seed the database first.</p>
        </div>
      </div>
    );
  }

  const handleProfileClick = (e: React.MouseEvent, person: any, caseId: string) => {
    e.stopPropagation(); // Prevent row selection when clicking the name

    // Parse existing age if it's a decimal (e.g., "50.4" -> years: 50, months: 5)
    let ageYears = '';
    let ageMonths = '';
    if (person.age && person.age !== 'Unknown' && person.age !== 'Unspecified') {
      const ageNum = parseFloat(person.age);
      if (!isNaN(ageNum)) {
        ageYears = Math.floor(ageNum).toString();
        ageMonths = Math.round((ageNum % 1) * 12).toString();
      }
    }

    setEditingProfile({
      id: person.id,
      profileId: person.profileId || person.id,
      name: person.name,
      gender: person.gender || 'U',
      age: person.age,
      caseId: caseId,
      kit: person.kit || 'GlobalFiler',
      ageType: 'age',
      dobDay: '',
      dobMonth: '',
      dobYear: '',
      ageYears,
      ageMonths
    });
    setIsEditModalOpen(true);
  };

  const handleSaveProfile = () => {
    if (!editingProfile) return;

    // Calculate age value based on ageType
    // DOB takes precedence over estimated age for generational sorting
    let ageValue: string | null = null;

    if (editingProfile.ageType === 'dob' && editingProfile.dobYear) {
      // Use DOB - calculate decimal age for storage
      const calculated = calculateAgeFromDOB(editingProfile.dobDay, editingProfile.dobMonth, editingProfile.dobYear);
      if (calculated) {
        ageValue = calculated.decimal;
      }
    } else if (editingProfile.ageType === 'age' && (editingProfile.ageYears || editingProfile.ageMonths)) {
      // Use estimated age as decimal
      ageValue = calculateDecimalAge(editingProfile.ageYears, editingProfile.ageMonths);
    }

    // Update profile via API - only send editable fields
    updateProfile.mutate({
      id: editingProfile.id,
      data: {
        name: editingProfile.name,
        gender: editingProfile.gender,
        age: ageValue,
        dobDay: editingProfile.ageType === 'dob' ? editingProfile.dobDay : null,
        dobMonth: editingProfile.ageType === 'dob' ? editingProfile.dobMonth : null,
        dobYear: editingProfile.ageType === 'dob' ? editingProfile.dobYear : null,
      }
    }, {
      onSuccess: () => {
        setIsEditModalOpen(false);
      }
    });
  };

  return (
    <div className="min-h-screen bg-background p-6 space-y-6">
      {/* Header */}
      <header className="flex items-center justify-between pb-6 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Dna className="w-8 h-8 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-foreground">ORCA</h1>
            <p className="text-sm text-muted-foreground">Familial Search & Pedigree Analysis</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Case Selector */}
          {cases && cases.length > 1 && (
            <Select
              value={currentCase?.id || ''}
              onValueChange={(value) => setSelectedCaseId(value)}
            >
              <SelectTrigger className="w-[200px]" data-testid="select-case">
                <SelectValue placeholder="Select case" />
              </SelectTrigger>
              <SelectContent>
                {cases.map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`case-option-${c.caseId}`}>
                    {c.caseId}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <Button variant="outline" size="sm">
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </Button>

          <Dialog>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" className="border-primary/20 hover:bg-primary/5 text-primary">
                <List className="w-4 h-4 mr-2" />
                Pass 1: Combinatorial List
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Pass 1: Combinatorial Hypotheses</DialogTitle>
                <DialogDescription>
                  Raw list of all potential relationships derived from non-zero Likelihood Ratios.
                </DialogDescription>
              </DialogHeader>
              <Pass1CombinatorialList kinshipRecords={data} />
            </DialogContent>
          </Dialog>

          <Dialog>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90">
                <Network className="w-4 h-4 mr-2" />
                Global Pedigree
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Global Pedigree Reconstruction</DialogTitle>
                <DialogDescription>
                  Combining all pairwise relationships into logical family trios and trees.
                </DialogDescription>
              </DialogHeader>
              <ComprehensiveFamilyTree kinshipRecords={data} />
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <div className="space-y-6">
        {/* Kinship Matrix */}
        <Card className="shadow-sm border-border/60">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline" className="font-mono text-[10px] uppercase tracking-widest bg-muted">Raw Data</Badge>
                  <CardTitle className="text-lg font-medium">Kinship Matrix</CardTitle>
                </div>
                <CardDescription>Case: {currentCase.caseId} • Created: {new Date(currentCase.createdDate).toLocaleDateString()}</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <Search className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <Filter className="w-4 h-4" />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                      data-testid="button-delete-case"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Case</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete case "{currentCase.caseId}"? This will permanently remove all profiles and kinship records associated with this case. This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        onClick={() => {
                          deleteCase.mutate(currentCase.id, {
                            onSuccess: () => {
                              setSelectedCaseId(null);
                            }
                          });
                        }}
                        data-testid="button-confirm-delete-case"
                      >
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-auto">
              <Table className="table-fixed">
                <TableHeader className="bg-muted/50">
                  <TableRow>
                    <TableHead
                      className="text-xs border-r border-border relative"
                      style={{ width: columnWidths.relationship }}
                    >
                      <div className="leading-tight">Hypothesized<br />Relationship</div>
                      <div
                        className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-primary/50"
                        onMouseDown={(e) => handleMouseDown(e, 'relationship')}
                      />
                    </TableHead>
                    <TableHead
                      className="cursor-pointer hover:text-primary transition-colors border-r border-border relative"
                      style={{ width: columnWidths.relative1 }}
                      onClick={() => handleSort('relative1')}
                    >
                      <div className="flex items-center gap-2">
                        Relative 1
                        <ArrowUpDown className="w-3 h-3 opacity-50" />
                      </div>
                      <div
                        className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-primary/50"
                        onMouseDown={(e) => { e.stopPropagation(); handleMouseDown(e, 'relative1'); }}
                      />
                    </TableHead>
                    <TableHead
                      className="cursor-pointer hover:text-primary transition-colors border-r border-border relative"
                      style={{ width: columnWidths.relative2 }}
                      onClick={() => handleSort('relative2')}
                    >
                      <div className="flex items-center gap-2">
                        Relative 2
                        <ArrowUpDown className="w-3 h-3 opacity-50" />
                      </div>
                      <div
                        className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-primary/50"
                        onMouseDown={(e) => { e.stopPropagation(); handleMouseDown(e, 'relative2'); }}
                      />
                    </TableHead>
                    <TableHead
                      className="text-xs border-r border-border relative"
                      style={{ width: columnWidths.commonLoci }}
                    >
                      <div className="flex justify-center">
                        <div className="text-left leading-tight">Common<br />Loci</div>
                      </div>
                      <div
                        className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-primary/50"
                        onMouseDown={(e) => handleMouseDown(e, 'commonLoci')}
                      />
                    </TableHead>
                    <TableHead
                      className="text-xs border-r border-border relative"
                      style={{ width: columnWidths.matchingLoci }}
                    >
                      <div className="flex justify-center">
                        <div className="text-left leading-tight">Matching<br />Loci</div>
                      </div>
                      <div
                        className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-primary/50"
                        onMouseDown={(e) => handleMouseDown(e, 'matchingLoci')}
                      />
                    </TableHead>
                    <TableHead
                      className="text-center text-xs whitespace-nowrap border-r border-border relative"
                      style={{ width: columnWidths.mac }}
                    >
                      MAC
                      <div
                        className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-primary/50"
                        onMouseDown={(e) => handleMouseDown(e, 'mac')}
                      />
                    </TableHead>
                    <TableHead
                      className="text-center cursor-pointer hover:text-primary transition-colors text-xs whitespace-nowrap border-r border-border relative"
                      style={{ width: columnWidths.parentChildLR }}
                      onClick={() => handleSort('parentChildLR')}
                    >
                      Parent/Child LR
                      <div
                        className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-primary/50"
                        onMouseDown={(e) => { e.stopPropagation(); handleMouseDown(e, 'parentChildLR'); }}
                      />
                    </TableHead>
                    <TableHead
                      className="text-center cursor-pointer hover:text-primary transition-colors text-xs whitespace-nowrap border-r border-border relative"
                      style={{ width: columnWidths.fullSiblingLR }}
                      onClick={() => handleSort('fullSiblingLR')}
                    >
                      Full Sibling LR
                      <div
                        className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-primary/50"
                        onMouseDown={(e) => { e.stopPropagation(); handleMouseDown(e, 'fullSiblingLR'); }}
                      />
                    </TableHead>
                    <TableHead
                      className="text-xs cursor-pointer hover:text-primary transition-colors relative"
                      style={{ width: columnWidths.extendedLR }}
                      onClick={() => handleSort('extendedFamilyLR')}
                    >
                      <div className="flex justify-center">
                        <div className="text-left leading-tight">Extended LR<br />(2nd Degree)</div>
                      </div>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedData.map((record) => (
                    <TableRow
                      key={record.id}
                      className={cn(
                        "cursor-pointer transition-colors hover:bg-muted/40",
                        selectedRecord === record.id && "bg-primary/5 border-l-2 border-l-primary"
                      )}
                      onClick={() => setSelectedRecord(record.id)}
                    >
                      <TableCell className="font-medium text-xs border-r border-border">
                        {record.relationship}
                      </TableCell>
                      <TableCell className="border-r border-border">
                        <div
                          className="flex flex-col cursor-pointer hover:bg-slate-100 p-1 rounded -ml-1 transition-colors"
                          onClick={(e) => handleProfileClick(e, record.relative1, record.caseId)}
                        >
                          <div className="flex items-center gap-2 font-medium text-sm text-primary hover:underline">
                            {record.relative1.name}
                            {getGenderIcon(record.relative1.gender)}
                          </div>
                          <span className="text-[10px] text-muted-foreground mt-0.5">
                            Age: {record.relative1.age && record.relative1.age !== 'Unknown' ? record.relative1.age : 'Unspecified'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="border-r border-border">
                        <div
                          className="flex flex-col cursor-pointer hover:bg-slate-100 p-1 rounded -ml-1 transition-colors"
                          onClick={(e) => handleProfileClick(e, record.relative2, record.caseId)}
                        >
                          <div className="flex items-center gap-2 font-medium text-sm text-primary hover:underline">
                            {record.relative2.name}
                            {getGenderIcon(record.relative2.gender)}
                          </div>
                          <span className="text-[10px] text-muted-foreground mt-0.5">
                            Age: {record.relative2.age && record.relative2.age !== 'Unknown' ? record.relative2.age : 'Unspecified'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs border-r border-border">{record.commonLoci}</TableCell>
                      <TableCell className="text-right font-mono text-xs border-r border-border">{record.matchingAlleles}</TableCell>
                      <TableCell className="text-right font-mono text-xs border-r border-border">{record.mac < 1 ? record.mac.toFixed(2) : record.mac}</TableCell>
                      <TableCell className="text-right font-mono text-xs border-r border-border">
                        <div className={cn(
                          "px-2 py-1 rounded inline-block",
                          record.parentChildLR > 100 ? "bg-green-50 text-green-700 font-bold" : ""
                        )}>
                          {formatNumber(record.parentChildLR)}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs border-r border-border">
                        <div className={cn(
                          "px-2 py-1 rounded inline-block",
                          record.fullSiblingLR > 100 ? "bg-blue-50 text-blue-700 font-bold" : ""
                        )}>
                          {formatNumber(record.fullSiblingLR)}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs">
                        <div className={cn(
                          "px-2 py-1 rounded inline-block",
                          record.extendedFamilyLR > 100 ? "bg-amber-50 text-amber-700 font-bold" : ""
                        )}>
                          {formatNumber(record.extendedFamilyLR)}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Edit Profile Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-slate-900">
              Edit Reference Profile {editingProfile?.name} for <span className="text-blue-600">{editingProfile?.caseId}</span>
            </DialogTitle>
          </DialogHeader>

          {editingProfile && (
            <div className="grid gap-6 py-4">
              <div className="grid gap-2">
                <Label htmlFor="kit" className="text-sm font-medium text-slate-700 after:content-['*'] after:ml-0.5 after:text-red-500">Kit</Label>
                <Select defaultValue={editingProfile.kit}>
                  <SelectTrigger id="kit">
                    <SelectValue placeholder="Select Kit" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GlobalFiler">GlobalFiler</SelectItem>
                    <SelectItem value="PowerPlex">PowerPlex</SelectItem>
                    <SelectItem value="VeriFiler">VeriFiler</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="name" className="text-sm font-medium text-slate-700 after:content-['*'] after:ml-0.5 after:text-red-500">Name</Label>
                <Input
                  id="name"
                  defaultValue={editingProfile.name}
                  onChange={(e) => setEditingProfile({ ...editingProfile, name: e.target.value })}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="sex" className="text-sm font-medium text-slate-700">Sex</Label>
                <Select
                  defaultValue={editingProfile.gender === 'M' ? 'Male' : 'Female'}
                  onValueChange={(val) => setEditingProfile({ ...editingProfile, gender: val === 'Male' ? 'M' : 'F' })}
                >
                  <SelectTrigger id="sex">
                    <SelectValue placeholder="Select Sex" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-3">
                <div className="flex items-center gap-4">
                  <Label className="text-sm font-medium text-slate-700 w-8">Age</Label>
                  <RadioGroup
                    value={editingProfile.ageType}
                    className="flex gap-6"
                    onValueChange={(val) => setEditingProfile({ ...editingProfile, ageType: val as 'dob' | 'age' })}
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="dob" id="dob" />
                      <Label htmlFor="dob" className="font-normal text-slate-600">Date Of Birth</Label>
                    </div>
                    <div className={cn(
                      "flex items-center space-x-2",
                      editingProfile.dobYear && "opacity-50"
                    )}>
                      <RadioGroupItem value="age" id="age" disabled={!!editingProfile.dobYear} />
                      <Label htmlFor="age" className={cn(
                        "font-normal text-slate-600",
                        editingProfile.dobYear && "text-slate-400"
                      )}>Age (Estimate)</Label>
                    </div>
                  </RadioGroup>
                </div>

                {editingProfile.ageType === 'dob' ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-4 mt-1">
                      <div className="grid gap-1.5">
                        <Label htmlFor="day" className="text-xs text-slate-500">Day</Label>
                        <Input
                          id="day"
                          className="h-9"
                          type="number"
                          min="1"
                          max="31"
                          placeholder="DD"
                          value={editingProfile.dobDay}
                          onChange={(e) => setEditingProfile({ ...editingProfile, dobDay: e.target.value })}
                        />
                      </div>
                      <div className="grid gap-1.5">
                        <Label htmlFor="month" className="text-xs text-slate-500">Month</Label>
                        <Input
                          id="month"
                          className="h-9"
                          type="number"
                          min="1"
                          max="12"
                          placeholder="MM"
                          value={editingProfile.dobMonth}
                          onChange={(e) => setEditingProfile({ ...editingProfile, dobMonth: e.target.value })}
                        />
                      </div>
                      <div className="grid gap-1.5">
                        <Label htmlFor="year" className="text-xs text-slate-500 after:content-['*'] after:ml-0.5 after:text-red-500">Year</Label>
                        <Input
                          id="year"
                          className="h-9"
                          type="number"
                          min="1900"
                          max={new Date().getFullYear()}
                          placeholder="YYYY"
                          value={editingProfile.dobYear}
                          onChange={(e) => setEditingProfile({ ...editingProfile, dobYear: e.target.value })}
                        />
                      </div>
                    </div>
                    {editingProfile.dobYear && (() => {
                      const calculated = calculateAgeFromDOB(editingProfile.dobDay, editingProfile.dobMonth, editingProfile.dobYear);
                      if (calculated) {
                        return (
                          <div className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 px-3 py-2 rounded-md">
                            <span className="font-medium">Calculated Age:</span>
                            <span className="text-blue-600 font-semibold">{calculated.decimal}</span>
                            <span className="text-slate-400">({calculated.years} years, {calculated.months} months)</span>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-4 mt-1 max-w-xs">
                      <div className="grid gap-1.5">
                        <Label htmlFor="ageYears" className="text-xs text-slate-500">Years</Label>
                        <Input
                          id="ageYears"
                          className="h-9"
                          type="number"
                          min="0"
                          max="120"
                          placeholder="Years"
                          value={editingProfile.ageYears}
                          onChange={(e) => setEditingProfile({ ...editingProfile, ageYears: e.target.value })}
                        />
                      </div>
                      <div className="grid gap-1.5">
                        <Label htmlFor="ageMonths" className="text-xs text-slate-500">Months</Label>
                        <Input
                          id="ageMonths"
                          className="h-9"
                          type="number"
                          min="0"
                          max="11"
                          placeholder="Months"
                          value={editingProfile.ageMonths}
                          onChange={(e) => setEditingProfile({ ...editingProfile, ageMonths: e.target.value })}
                        />
                      </div>
                    </div>
                    {(editingProfile.ageYears || editingProfile.ageMonths) && (
                      <div className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 px-3 py-2 rounded-md max-w-xs">
                        <span className="font-medium">Calculated Age:</span>
                        <span className="text-blue-600 font-semibold">
                          {calculateDecimalAge(editingProfile.ageYears, editingProfile.ageMonths)}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="sm:justify-start">
            <Button type="button" onClick={handleSaveProfile} className="bg-slate-100 text-slate-900 hover:bg-slate-200 border border-slate-200">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
